import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { gsap } from '@/lib/gsap'
import { hasFinePointer, prefersReducedMotion } from '@/lib/motion'
import { cn } from '@/lib/utils'

interface MagneticProps {
    children: ReactNode
    strength?: number
    className?: string
}

/** Pulls its content toward the cursor while hovered, then springs back. */
const Magnetic: FC<MagneticProps> = ({ children, strength = 0.35, className }): ReactNode => {
    const root = useRef<HTMLSpanElement>(null)

    useEffect(() => {
        const element = root.current

        if (!element || !hasFinePointer() || prefersReducedMotion()) return

        const xTo = gsap.quickTo(element, 'x', { duration: 0.8, ease: 'elastic.out(1, 0.4)' })
        const yTo = gsap.quickTo(element, 'y', { duration: 0.8, ease: 'elastic.out(1, 0.4)' })

        const onMove = (event: PointerEvent): void => {
            const rect = element.getBoundingClientRect()

            xTo((event.clientX - (rect.left + rect.width / 2)) * strength)
            yTo((event.clientY - (rect.top + rect.height / 2)) * strength)
        }

        const onLeave = (): void => {
            xTo(0)
            yTo(0)
        }

        element.addEventListener('pointermove', onMove)
        element.addEventListener('pointerleave', onLeave)

        return () => {
            element.removeEventListener('pointermove', onMove)
            element.removeEventListener('pointerleave', onLeave)
        }
    }, [strength])

    return (
        <span ref={root} className={cn('inline-block will-change-transform', className)}>
            {children}
        </span>
    )
}

export default Magnetic
